/**
 * Container template for a component.
 */

/* Import the component from the component path */
import Component from '../components/ProjectDetails'

import { connect } from 'react-redux'
import { Dispatch, Action } from 'redux'
import { createSelector } from 'reselect'

/* Import RootStoreState */
import { RootStoreState } from 'modules/root'
import * as dt from 'modules/database/types'
import * as da from 'modules/database/actions'
import * as a from '../actions'
import * as t from '../types'
import * as f from '../functions'
import * as pra from 'modules/product/actions'
import * as ut from 'modules/user/types'

/**
 * Interface for properties that the container passes to the component.
 */
export interface Props {
	project: DeepReadonly<dt.ProjectWithProducts>
	productsByType: t.ProductsByType
	deletedProducts: DeepReadonly<dt.Product>[]
	deletedProductsByType: t.ProductsByType
	user: DeepReadonly<ut.User>
	admin?: boolean
}

export interface OwnProps {
	projectId: dt.ProjectID
}

/**
 * Interface for action callbacks that the container exposes to the component.
 * The component's `this.props` is typed `Props & Actions`.
 */
export interface Actions {
	onOpenEditForm: () => void
	onNewProduct: (type: dt.ProductType) => void
	onEditProduct: (product: dt.Product) => void
	onDeleteProduct: (productId: dt.ProductID) => void
	onUndeleteProduct: (productId: dt.ProductID) => void
	onChangeProject: (newProject: dt.ProjectWithProducts) => void
	onDuplicateProject: (project: dt.ProjectWithProducts) => void
}

const currentProject = (state: RootStoreState) => state.project.currentProject!

/* Memoise the derived product lists so they are only recomputed when the current project changes,
   rather than on every store change.
 */
const deletedProductsSelector = createSelector(currentProject, (project) => f.deletedProducts(project))
const productsByTypeSelector = createSelector(currentProject, (project) => f.productsByType(f.allProducts(project)))
const deletedProductsByTypeSelector = createSelector(deletedProductsSelector, (deletedProducts) => f.productsByType(deletedProducts))

/** Populate the Props from the store state. */
const mapStateToProps = (state: RootStoreState, ownProps: OwnProps): Props => {
	return {
		project: currentProject(state),
		productsByType: productsByTypeSelector(state),
		deletedProducts: deletedProductsSelector(state),
		deletedProductsByType: deletedProductsByTypeSelector(state),
		user: state.user.user,
		admin: state.user.userAdministrative?.admin,
	}
}

/** Populate the Actions with the callbacks for the component. */
const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps): Actions => ({
	onOpenEditForm: () => {
		dispatch(a.editProject(ownProps.projectId))
	},
	onNewProduct: (productType) => {
		dispatch(pra.newProduct({ projectId: ownProps.projectId, productType }))
	},
	onEditProduct: (product) => {
		dispatch(pra.editProduct({ projectId: ownProps.projectId, product }))
	},
	onDeleteProduct: (productId) => {
		dispatch(da.deleteProduct({ projectId: ownProps.projectId, productId }))
	},
	onUndeleteProduct: (productId) => {
		dispatch(da.undeleteProduct({ projectId: ownProps.projectId, productId }))
	},
	onChangeProject: (newProject) => {
		dispatch(a.updateEditingProject(newProject))
		dispatch(da.updateProject(newProject))
	},
	onDuplicateProject: (project) => {
		dispatch(a.duplicateProject(project))
	},
})

export default connect(mapStateToProps, mapDispatchToProps)(Component)
